import React, { useEffect, useState } from "react";
import { api, assetUrl } from "../lib/api";
import EnquiryDialog from "../components/site/EnquiryDialog";
import CommercialPlan from "../components/site/CommercialPlan";
import AmenitiesStrip from "../components/site/AmenitiesStrip";
import { Send, FileText, Download, Building2, X } from "lucide-react";

const SectionPage = ({ sectionKey, fallbackTitle, fallbackIntro }) => {
  const [sec, setSec] = useState({});
  const [docs, setDocs] = useState([]);
  const [enq, setEnq] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/sections/${sectionKey}`).then((r) => setSec(r.data || {})).catch(() => {});
    api.get(`/documents?category=${sectionKey}`).then((r) => setDocs(r.data)).catch(() => {});
  }, [sectionKey]);

  const gallery = sec.gallery || [];

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      {/* hero */}
      <section className="relative overflow-hidden border-b border-[#ffffff14]">
        {gallery[0] ? (
          <img src={assetUrl(gallery[0])} alt={sec.title || fallbackTitle} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        ) : (
          <div className="absolute inset-0 bg-[#0F1B15]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#12201A] via-[#12201A]/70 to-[#12201A]/60" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-8 py-20">
          <p className="eyebrow text-[#C9B79C] mb-4 flex items-center gap-2"><Building2 size={14} /> Value Driver</p>
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="font-display font-light text-4xl md:text-6xl text-[#F3EEE3]">{sec.title || fallbackTitle}</h1>
            {sec.status_label && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#C9B79C]/40 bg-[#C9B79C]/15 px-4 py-1.5 text-sm text-[#F3EEE3]">
                <span className="w-2 h-2 rounded-full bg-[#C9B79C]" /> {sec.status_label}
              </span>
            )}
          </div>
          <p className="text-[#F3EEE3]/75 mt-5 max-w-2xl text-lg leading-relaxed">{sec.intro || fallbackIntro}</p>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14">
        {sec.body && <p className="text-[#F3EEE3]/75 max-w-3xl leading-relaxed whitespace-pre-line">{sec.body}</p>}

        {sectionKey === "commercial" && <div className="mt-12"><CommercialPlan planImage={sec.plan_image} /></div>}

        {gallery.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {gallery.map((g, i) => (
              <button key={i} onClick={() => setLightbox(assetUrl(g))} data-testid={`section-img-${i}`} className="group rounded-xl overflow-hidden aspect-[4/3] bg-[#0C1510]">
                <img src={assetUrl(g)} alt={`${sec.title || fallbackTitle} — photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        )}

        {sectionKey === "apartment" && <AmenitiesStrip />}

        {docs.length > 0 && (
          <>
            <h2 className="font-display text-2xl text-[#F3EEE3] mt-14 mb-5">Documents</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {docs.map((d) => (
                <a key={d.id} href={assetUrl(`/api/documents/${d.id}/download`)} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-5 py-4 hover:border-[#C9B79C]/40">
                  <span className="flex items-center gap-3 text-[#F3EEE3] text-sm"><FileText size={16} className="text-[#C9B79C]" /> {d.title}</span>
                  <Download size={15} className="text-[#F3EEE3]/50" />
                </a>
              ))}
            </div>
          </>
        )}

        <div className="mt-14 rounded-2xl border border-[#C9B79C]/25 bg-[#0F1B15] p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <p className="text-[#F3EEE3] text-lg">Interested in this opportunity?</p>
          <button onClick={() => setEnq(true)} data-testid="section-enquire-btn" className="inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-7 py-3.5 rounded-full font-medium hover:bg-white transition-colors">
            <Send size={16} /> Request Information
          </button>
        </div>

        {sec.disclaimer && (
          <p className="text-[0.62rem] tracking-widest uppercase text-[#F3EEE3]/40 mt-8">{sec.disclaimer}</p>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-[#0C1510]/95 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button className="absolute top-5 right-5 text-[#F3EEE3]/70 hover:text-[#F3EEE3]"><X size={28} /></button>
          <img src={lightbox} alt={sec.title || fallbackTitle} className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg" />
        </div>
      )}

      <EnquiryDialog open={enq} onClose={() => setEnq(false)} context={{ label: sec.title || fallbackTitle, source: sectionKey }} />
    </div>
  );
};

export default SectionPage;
